import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import { useCallback, useContext, useEffect, useState } from 'react';
import { InventoryTypeId, InventoryTypeKey } from '~/constants/inventoryType';
import { useFavoritesIds, useIsLoggedIn } from '~/context/common/UserContext';
import { useToggleFavorite } from '~/hooks/common/favorites';
import Chevron from '~/icons/Chevron';
import HeartWithBorder from '~/icons/HeartWithBorder';
import { getNativeVdpPayload } from '~/native/home/utils';
import { VehicleTag } from '~/services/common/contracts/InventoryResponse';
import getImageToUse from './getImageToUse';
import getVehicleStatus from './getVehicleStatus';
import StatusTag from './StatusTag';
import { LinkBoldCopy, LinkCopy } from '../LinkCard.styles';

export enum LinkType {
  Favorite = 'favorites',
  Recent = 'viewed',
  Suggested = 'suggested'
}

export type LinkVehicle = {
  jellyBeanMobileUrl?: string;
  make: string;
  model: string;
  price: number;
  stockNumber: number;
  trim: string;
  vehicleInventoryType: InventoryTypeId | InventoryTypeKey;
  vehicleTags?: VehicleTag[];
  vehicleUnlockDateTime?: string;
  year: string;
  vehicleId: number;
};

type OwnProps = { vehicle: LinkVehicle; type: LinkType };

export default function LinkCard({ vehicle, type }: OwnProps) {
  const { year, make, model, trim, price, vehicleId, stockNumber } = vehicle;
  const { trackEvent } = useContext(AnalyticsContext);
  const toggleFavorite = useToggleFavorite();
  const isLoggedIn = useIsLoggedIn();
  const status = getVehicleStatus({ ...vehicle });
  const hasStatus = status !== null;
  const favoriteIds = useFavoritesIds();
  const [isFavoritedLocally, setFavoritedLocally] = useState(
    favoriteIds.includes(vehicleId)
  );

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      toggleFavorite({ vehicleId, stockNumber, nativeEventSource: 'home' });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Clicked - Home feed card', {
        description: 'VDP card',
        type,
        item: 'heart',
        vehicleId
      });
    },
    [isLoggedIn, stockNumber, toggleFavorite, trackEvent, type, vehicleId]
  );

  useEffect(() => {
    setFavoritedLocally(favoriteIds.includes(vehicle.vehicleId));
  }, [favoriteIds, vehicle.vehicleId]);

  const yearMakeModel = `${year} ${make} ${model}`;

  return (
    <a
      href={`/vehicle/${vehicleId}`}
      data-analytics-id="Home feed card"
      data-analytics-attributes={JSON.stringify({
        description: 'VDP card',
        type,
        item: 'vehicle'
      })}
      className="py-16 first:pt-0 last:pb-0"
      data-native-payload={getNativeVdpPayload(vehicleId)}
      data-qa="vdp-link-vehicle"
    >
      <div className="grid grid-cols-[128px_1fr_16px] items-center gap-16  text-blue-6 ">
        <div id="thumbnail" className="relative h-[96px]">
          <img
            src={getImageToUse({ ...vehicle })}
            alt="vehicle image"
            className="rounded-[6px]"
          />
          <Clickable
            onClick={handleFavorite}
            data-analytics-id={null}
            data-qa="favorite vehicle"
            className="pointer-events-auto absolute right-4 top-4"
          >
            <HeartWithBorder
              darkBlue
              isFavorited={isFavoritedLocally}
              className="h-20 w-20"
            />
          </Clickable>
        </div>
        <div className="flex h-[96px] flex-col justify-center gap-4 overflow-hidden">
          <StatusTag status={status} />
          <LinkBoldCopy truncate={hasStatus} title={yearMakeModel}>
            {yearMakeModel}
          </LinkBoldCopy>
          <LinkCopy>{trim}</LinkCopy>
          <LinkBoldCopy>${price.toLocaleString()}</LinkBoldCopy>
        </div>
        <Chevron />
      </div>
    </a>
  );
}
